import { useFlags } from 'gatsby-plugin-launchdarkly';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { createOrder, getOrder } from 'utils/api/cartApis';
import { saveOrder } from 'utils/cartHelpers';
import { Flags } from 'utils/constants/featureFlags';
import { getStore } from 'utils/helpers/localStorageHelpers';

import CartContext from '../Cart.context';

export type CartProviderProps = {
  children: React.ReactNode;
};

/**
 * This component is meant to be defined near the root of the application
 * so that all components can access the cart state.
 */
function CartProvider(props: CartProviderProps): JSX.Element {
  const flags = useFlags();
  const isCartRefactorEnabled = flags[Flags.CartRefactorEnabled] === true;

  const [isCartOpen, setIsCartOpen] = useState(false);

  const openCart = () => {
    setIsCartOpen(true);
  };

  const closeCart = () => {
    setIsCartOpen(false);
  };

  const [orderId, setOrderId] = useState<number | undefined | null>(undefined);

  /**
   * Loads the current order from browser storage and sets the orderId state
   */
  useEffect(() => {
    const browserStorage = getStore();

    const currentOrder =
      browserStorage.current_order && JSON.parse(browserStorage.current_order);
    const orderId = currentOrder?.id;

    if (orderId) {
      setOrderId(orderId);
    } else {
      setOrderId(null);
    }
  }, []);

  /**
   * Fetch the current order from the API with the defined orderId
   */
  const { data: fetchedOrderResponse, isError } = useQuery(
    `orders-${orderId}`,
    () => getOrder(orderId || 0),
    {
      enabled: isCartRefactorEnabled && Boolean(orderId),
      retry: 1,
      onSuccess: (response) => {
        // TODO: save the orderId instead of the full order and save only on create
        saveOrder(response);
      },
    },
  );

  /**
   * Creates a new order in the API and saves it in the browser storage and
   * saves the orderId in the local state
   */
  useQuery(`create-order`, () => createOrder(), {
    enabled: isCartRefactorEnabled && (isError || orderId === null),
    retry: 0,
    onSuccess: (response) => {
      setOrderId(response.data.id);
    },
  });

  // console.log('order', fetchedOrderResponse?.data);

  return (
    <CartContext.Provider
      value={{
        openCart,
        closeCart,
        isCartOpen,
        order: fetchedOrderResponse?.data,
        isCartRefactorEnabled,
      }}
    >
      {props.children}
    </CartContext.Provider>
  );
}

export default CartProvider;
